import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { isEmpty } from "lodash";

import {
  getVenues,
  deleteVenue,
  createVenue,
  updateVenue,
} from "./sliceReducer";
import {
  DELETE_VENUE,
  GET_ALL_VENUES,
  CREATE_VENUE,
  EDIT_VENUE,
  GET_SEARCH_VENUES,
} from "../../constants/endpoints";
import {
  VENUE_FIELDS_TITLE,
  defaultParamsViewTable,
} from "../../constants/data";
import { TITLE_BUTTONS, PAGES_TITLES, TITLES } from "../../constants/titles";
import { getFillDtoVenues } from "../../utils/getFillDto";
import { updateDataVenues } from "../../utils/getDataForTable";
import { addSearchValue } from "../../Redux/Reducers/commonReducer";

import { MAPS_TYPES } from "../../utils/mapsAutofill";

import Header from "../../containers/Header";
import DataTable from "../../containers/DataTable";
import ConfirmDialog from "../../components/ConfirmDialog";
import FieldOfCreate from "../../components/FieldOfCreate";
import ButtonsGroup from "../../components/ButtonsGroup";
import CreateFromLinkDialog from "../../containers/CreateFromLinkDialog";

const useStyles = makeStyles({
  root: {
    display: "flex",
    justifyContent: "center",
  },
  progress: {
    marginTop: "50px",
  },
});

const Venues = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { fetchData, loading } = useSelector((state) => state.venues);
  const { search } = useSelector((state) => state.common);

  const [pagination, setPagination] = useState({ ...defaultParamsViewTable });
  const [dtos, setDtos] = useState([]);
  const [defaultValueForm, setDefaultValueForm] = useState(null);
  const [selectedDto, setSelectedDto] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectID, setSelectID] = useState(null);
  const [open, setOpen] = useState(false);
  const [openFromLink, setOpenFromLink] = useState(false);

  const dataForForms = Object.entries(VENUE_FIELDS_TITLE).filter(
    (e) => e[0] !== "id"
  );

  const currentVenuesEndpoint = search ? GET_SEARCH_VENUES(search) : GET_ALL_VENUES;

  const reloadVenues = (payload = pagination) => {
    return dispatch(
      getVenues({
        endpoint: currentVenuesEndpoint,
        params: {
          ...defaultParamsViewTable,
          ...payload,
        },
      }),
    );
  };

  const normalizeVenueDefaultValue = (dto) => {
    const v = dto || {};
    const a = v.addressDto || {};

    return {
      ...v,
      ...a,
      state: typeof a.state === "string" ? a.state.trim() : a.state,
      addressDto: a,
    };
  };


  const handleClickOpen = (data) => {
    setOpen(true);

    if (data?.id) {
      const fullDto = dtos.find((v) => v.id === data.id) || data;

      setSelectedDto(fullDto);
      setDefaultValueForm(normalizeVenueDefaultValue(fullDto));
    } else {
      setSelectedDto({});
      setDefaultValueForm({});
    }
  };



  const handleClose = () => {
    setDefaultValueForm(null);
    setOpen(false);
    setSelectedDto({});
  };

  const handleOpenFromLink = () => setOpenFromLink(true);
  const handleCloseFromLink = () => setOpenFromLink(false);

  /**
   * Receives the drafts from CreateFromLinkDialog and opens the normal create form
   * pre-filled. Nothing has been persisted at this point — the admin still reviews
   * and submits through the existing createVenue path.
   */
  const handleApplyFromLink = ({ venueValues }) => {
    setOpenFromLink(false);

    if (!venueValues) return;

    setSelectedDto({});
    setDefaultValueForm(venueValues);
    setOpen(true);
  };

  const handleOpenModal = (id) => {
    setSelectID(id);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectID(null);
    setOpenModal(false);
  };

  // IMPORTANT: return promise + unwrap so ButtonsGroup can await and only close on success
  const deleteOneVenue = () => {
    if (!selectID) {
      return Promise.reject(new Error("No venue selected for deletion."));
    }

    return dispatch(deleteVenue(DELETE_VENUE(selectID)))
      .unwrap()
      .then(() => reloadVenues());
  };

  const createOneVenue = (params) => {
    return dispatch(
      createVenue({
        endpoint: CREATE_VENUE,
        data: getFillDtoVenues(params),
      })
    )
      .unwrap()
      .then(() => reloadVenues());
  };

  const updateOneVenue = (params, id, idAddress) => {
    return dispatch(
      updateVenue({
        endpoint: EDIT_VENUE,
        data: { ...getFillDtoVenues(params, idAddress), id },
      })
    )
      .unwrap()
      .then(() => reloadVenues());
  };

  // IMPORTANT: do NOT close modal until create/update succeeds
  const onSubmit = (params) => {
    if (!isEmpty(selectedDto)) {
      const { id } = selectedDto;
      const idAddress = selectedDto?.addressDto?.id;

      if (!id || !idAddress) {
        return Promise.reject(
          new Error("Cannot update venue: missing venue id or address id.")
        );
      }

      return updateOneVenue(params, id, idAddress).then(() => {
        handleClose();
      });
    }

    return createOneVenue(params).then(() => {
      handleClose();
    });
  };

  const onChangePage = (payload) => {
    setPagination((prev) => ({
      ...prev,
      ...payload,
    }));

    const params = {
      ...defaultParamsViewTable,
      ...payload,
    };

    dispatch(
      getVenues({
        endpoint: currentVenuesEndpoint,
        params,
      })
    );
  };

  useEffect(() => {
    dispatch(addSearchValue(""));
  }, [dispatch]);

  useEffect(() => {
    if (!isEmpty(fetchData)) {
      const { currentPage, totalElements, dtos: responseDtos } = fetchData;

      setPagination((state) => ({
        ...state,
        page: currentPage,
        total: totalElements,
      }));

      setDtos(responseDtos || []);
    }
  }, [fetchData]);

  useEffect(() => {
    dispatch(
      getVenues({
        endpoint: currentVenuesEndpoint,
        params: defaultParamsViewTable,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <React.Fragment>
      <Header
        handleClickOpen={handleClickOpen}
        title={PAGES_TITLES.VENUES}
        titleButton={TITLE_BUTTONS.CREATE_VENUE}
        secondaryTitleButton={TITLE_BUTTONS.CREATE_FROM_LINK}
        handleSecondaryClick={handleOpenFromLink}
      />

      <div className={classes.root}>
        {loading ? (
          <CircularProgress className={classes.progress} />
        ) : (
          <DataTable
            updateItem={handleClickOpen}
            fetchData={dtos}
            pagination={pagination}
            removeRow={handleOpenModal}
            onChangePage={onChangePage}
            updateData={updateDataVenues}
            fieldsRow={VENUE_FIELDS_TITLE}
          />
        )}
      </div>

      {open && (
        <ConfirmDialog open handleClose={handleClose} title={TITLES.CREATE_VENUE}>
          {!!defaultValueForm ? (
            <FieldOfCreate
              dataForForms={dataForForms}
              defaultValue={defaultValueForm}
              handleClose={handleClose}
              onSubmit={onSubmit}
            />
          ) : null}
        </ConfirmDialog>
      )}

      {openFromLink && (
        <ConfirmDialog
          open
          handleClose={handleCloseFromLink}
          title={TITLES.CREATE_VENUE_FROM_LINK}
          disableBackdropClose
        >
          <CreateFromLinkDialog
            open
            type={MAPS_TYPES.VENUE}
            onCancel={handleCloseFromLink}
            onApply={handleApplyFromLink}
          />
        </ConfirmDialog>
      )}

      {openModal && (
        <ConfirmDialog open handleClose={handleCloseModal} title={TITLES.DELETE_MODAL}>
          <ButtonsGroup
            handleClose={handleCloseModal}
            handleFunction={deleteOneVenue}
            submitTitle={TITLE_BUTTONS.DELETE}
            closeTitle={TITLE_BUTTONS.CANCEL}
          />
        </ConfirmDialog>
      )}
    </React.Fragment>
  );
};

export default Venues;
